import { Person, PersonCategory } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useCreatePerson, usePersons } from '@/hooks/api-hooks';
import { Button, Loader, TextInput, NumberInput, ActionIcon, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconId, IconMail, IconPhone, IconPhonePlus, IconUsers, IconSearch, IconPlus, IconX, IconUserPlus, IconPencil, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { TagIcon } from '@/lib/tag-utils';
import { notify } from '@/lib/notify';
import { PaxCategoryBadge, PaxCategorySelect } from './snippets';

const MIN_CHARS_TO_SEARCH = 2;

interface Props {
    onCreate?: (pax: Person) => void;
    onSelect?: (pax: Person) => void;
    onClose?: () => void;
    preloadName?: string;
}

export default function CreateOrSelectPerson({
    onCreate,
    onSelect,
    onClose,
    preloadName,
}: Props): JSX.Element {
    const { t } = useTranslation();
    const [name, setName] = useState(preloadName || '');

    // Top section search-only fields
    const [searchEmail, setSearchEmail] = useState('');
    const [searchPhone, setSearchPhone] = useState('');
    const [searchDocument, setSearchDocument] = useState('');
    const [activeSearchValue, setActiveSearchValue] = useState(preloadName || '');

    const [showSearchDoc, setShowSearchDoc] = useState(false);
    const [showSearchPhone, setShowSearchPhone] = useState(false);
    const [showSearchEmail, setShowSearchEmail] = useState(false);

    // Bottom section new-pax-only fields
    const [category, setCategory] = useState<PersonCategory | null>('pax');
    const [adults, setAdults] = useState<number | string>('');
    const [children, setChildren] = useState<number | string>('');
    const [infants, setInfants] = useState<number | string>('');
    const [seniors, setSeniors] = useState<number | string>('');
    const [guests, setGuests] = useState<number | string>('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [phone_2, setPhone2] = useState('');
    const [showPhone2, setShowPhone2] = useState(false);
    const [document_number, setDocument] = useState('');
    const [observations, setObservations] = useState('');
    const [showNewPaxSection, setShowNewPaxSection] = useState(false);

    const [isDropdownOpen, setIsDropdownOpen] = useState(
        Boolean(preloadName && preloadName.trim().length >= MIN_CHARS_TO_SEARCH)
    );
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

    const containerRef = useRef<HTMLDivElement>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const searchDocInputRef = useRef<HTMLInputElement>(null);
    const searchPhoneInputRef = useRef<HTMLInputElement>(null);
    const searchEmailInputRef = useRef<HTMLInputElement>(null);
    const docInputRef = useRef<HTMLInputElement>(null);
    const phoneInputRef = useRef<HTMLInputElement>(null);
    const phone2InputRef = useRef<HTMLInputElement>(null);
    const emailInputRef = useRef<HTMLInputElement>(null);

    // Compute search query dynamically based on top search fields only (min 2 chars)
    const getSearchQuery = () => {
        if (activeSearchValue.trim().length >= MIN_CHARS_TO_SEARCH) return activeSearchValue.trim();
        if (name.trim().length >= MIN_CHARS_TO_SEARCH) return name.trim();
        if (searchDocument.trim().length >= MIN_CHARS_TO_SEARCH) return searchDocument.trim();
        if (searchPhone.trim().length >= MIN_CHARS_TO_SEARCH) return searchPhone.trim();
        if (searchEmail.trim().length >= MIN_CHARS_TO_SEARCH) return searchEmail.trim();
        return '';
    };

    const searchQuery = getSearchQuery();
    const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 250);

    const {
        data: searchResponse,
        isLoading: isSearching
    } = usePersons({
        query: debouncedSearchQuery,
    }, {
        enabled: debouncedSearchQuery.length >= MIN_CHARS_TO_SEARCH
    });

    const matchingPaxs: Person[] = (searchResponse as any)?.rows || (searchResponse as any)?.data || [];

    const createPersonMutation = useCreatePerson();

    const handleSelectPax = (selectedPax: Person) => {
        onCreate?.(selectedPax);
        onSelect?.(selectedPax);
        setName('');
        setCategory(null);
        setAdults('');
        setChildren('');
        setInfants('');
        setSeniors('');
        setGuests('');
        setSearchEmail('');
        setSearchPhone('');
        setSearchDocument('');
        setEmail('');
        setPhone('');
        setPhone2('');
        setShowPhone2(false);
        setDocument('');
        setObservations('');
        setActiveSearchValue('');
        setShowSearchDoc(false);
        setShowSearchPhone(false);
        setShowSearchEmail(false);
        setShowNewPaxSection(false);
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        nameInputRef.current?.focus();
    };

    const save = () => {
        const trimmedName = name.trim();
        if (!trimmedName || trimmedName.length < 2 || createPersonMutation.isPending) return;

        createPersonMutation.mutate({
            name: trimmedName,
            email: email.trim() || undefined,
            category: category || undefined,
            adults: Number(adults) || 0,
            children: Number(children) || 0,
            infants: Number(infants) || 0,
            seniors: Number(seniors) || 0,
            guests: Number(guests) || 0,
            phone: phone.trim() || undefined,
            phone_2: phone_2.trim() || undefined,
            document_number: document_number.trim() || undefined,
            observations: observations.trim() || undefined,
        }, {
            onSuccess: (result) => {
                handleSelectPax(result.data);
            },
            onError: (err: any) => {
                const errorKey = err?.response?.data?.error || err?.error || err?.message || 'error';
                notify.error(t(errorKey), t('error'));
            }
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isDropdownOpen) {
            if (e.key === 'Enter') {
                e.preventDefault();
                save();
            }
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const maxIndex = matchingPaxs.length; // matchingPaxs.length represents the bottom create row
            setHighlightedIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const maxIndex = matchingPaxs.length;
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsDropdownOpen(false);
            setHighlightedIndex(-1);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedIndex >= 0 && highlightedIndex < matchingPaxs.length) {
                handleSelectPax(matchingPaxs[highlightedIndex]);
            } else {
                save();
            }
        }
    };

    // Close popover on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest('[data-combobox-dropdown], [data-combobox-option], [role="listbox"], [role="option"]')) {
                return;
            }
            if (containerRef.current && !containerRef.current.contains(target as Node)) {
                setIsDropdownOpen(false);
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (preloadName) {
            setName(preloadName);
            setActiveSearchValue(preloadName);
            if (preloadName.trim().length >= MIN_CHARS_TO_SEARCH) {
                setIsDropdownOpen(true);
            }
        }
    }, [preloadName]);

    const toggleSearchDoc = () => {
        setShowSearchDoc((prev) => {
            const next = !prev;
            if (next) setTimeout(() => searchDocInputRef.current?.focus(), 50);
            return next;
        });
    };

    const toggleSearchPhone = () => {
        setShowSearchPhone((prev) => {
            const next = !prev;
            if (next) setTimeout(() => searchPhoneInputRef.current?.focus(), 50);
            return next;
        });
    };

    const toggleSearchEmail = () => {
        setShowSearchEmail((prev) => {
            const next = !prev;
            if (next) setTimeout(() => searchEmailInputRef.current?.focus(), 50);
            return next;
        });
    };

    const canCreateNewPax = name.trim().length >= MIN_CHARS_TO_SEARCH;
    const hasRevealedSearchFields = showSearchDoc || showSearchPhone || showSearchEmail;
    const shouldShowPopover = isDropdownOpen && (debouncedSearchQuery.length >= MIN_CHARS_TO_SEARCH || canCreateNewPax);

    return (
        <div ref={containerRef} className="relative w-full max-w-full">
            {/* Primary Search / Name Input & Quick Search Toggles */}
            <div className="flex items-center gap-1.5 w-full">
                <div className="flex-1 min-w-0">
                    <TextInput
                        ref={nameInputRef}

                        tabIndex={0}
                        size="sm"
                        placeholder={t('passenger_name_input_placeholder')}
                        value={name}
                        disabled={createPersonMutation.isPending}
                        leftSection={<IconSearch size={16} className="text-slate-400" />}
                        onChange={(e) => {
                            const val = e.target.value;
                            setName(val);
                            setActiveSearchValue(val);
                            setIsDropdownOpen(true);
                            setHighlightedIndex(-1);
                        }}
                        onFocus={() => {
                            if (name.trim().length >= MIN_CHARS_TO_SEARCH || debouncedSearchQuery.length >= MIN_CHARS_TO_SEARCH) {
                                setIsDropdownOpen(true);
                            }
                        }}
                        onKeyDown={handleKeyDown}
                        rightSection={
                            isSearching ? <Loader size="xs" color='black' /> : null
                        }
                    />
                </div>

                {/* Search-only Quick Toggle Action Icons */}
                <div className="flex items-center gap-1 shrink-0">
                    <Tooltip label={t('search_by_phone')} withArrow>
                        <ActionIcon
                            tabIndex={-1}
                            variant={showSearchPhone || searchPhone ? 'light' : 'subtle'}
                            color={showSearchPhone || searchPhone ? 'blue' : 'gray'}
                            size={36}
                            radius="md"
                            onClick={toggleSearchPhone}
                        >
                            <IconPhone size={16} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t('search_by_document_number')} withArrow>
                        <ActionIcon
                            tabIndex={-1}
                            variant={showSearchDoc || searchDocument ? 'light' : 'subtle'}
                            color={showSearchDoc || searchDocument ? 'blue' : 'gray'}
                            size={36}
                            radius="md"
                            onClick={toggleSearchDoc}
                        >
                            <IconId size={16} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t('search_by_email')} withArrow>
                        <ActionIcon
                            tabIndex={-1}
                            variant={showSearchEmail || searchEmail ? 'light' : 'subtle'}
                            color={showSearchEmail || searchEmail ? 'blue' : 'gray'}
                            size={36}
                            radius="md"
                            onClick={toggleSearchEmail}
                        >
                            <IconMail size={16} />
                        </ActionIcon>
                    </Tooltip>
                </div>
            </div>

            {/* Revealed Search-By Inputs (Phone, Document, Email - for searching only) */}
            {hasRevealedSearchFields && (
                <div className="flex flex-wrap gap-2 pt-2 items-center bg-slate-50/90 p-2 rounded-lg border border-slate-200/80 mt-2">
                    {showSearchPhone && (
                        <TextInput
                            ref={searchPhoneInputRef}
                            size="xs"
                            type="tel"
                            placeholder={t('search_by_phone')}
                            leftSection={<IconPhone size={13} className="text-slate-400" />}
                            value={searchPhone}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSearchPhone(val);
                                setActiveSearchValue(val);
                                setIsDropdownOpen(true);
                                setHighlightedIndex(-1);
                            }}
                            onFocus={() => {
                                if (searchPhone.trim().length >= MIN_CHARS_TO_SEARCH || debouncedSearchQuery.length >= MIN_CHARS_TO_SEARCH) {
                                    setIsDropdownOpen(true);
                                }
                            }}
                            onKeyDown={handleKeyDown}
                            rightSection={
                                <ActionIcon
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    onClick={() => {
                                        setSearchPhone('');
                                        setActiveSearchValue('');
                                        setShowSearchPhone(false);
                                    }}
                                >
                                    <IconX size={11} />
                                </ActionIcon>
                            }
                            className="flex-1 min-w-[140px]"
                        />
                    )}

                    {showSearchDoc && (
                        <TextInput
                            ref={searchDocInputRef}
                            size="xs"
                            placeholder={t('search_by_document_number')}
                            leftSection={<IconId size={13} className="text-slate-400" />}
                            value={searchDocument}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSearchDocument(val);
                                setActiveSearchValue(val);
                                setIsDropdownOpen(true);
                                setHighlightedIndex(-1);
                            }}
                            onFocus={() => {
                                if (searchDocument.trim().length >= MIN_CHARS_TO_SEARCH || debouncedSearchQuery.length >= MIN_CHARS_TO_SEARCH) {
                                    setIsDropdownOpen(true);
                                }
                            }}
                            onKeyDown={handleKeyDown}
                            rightSection={
                                <ActionIcon
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    onClick={() => {
                                        setSearchDocument('');
                                        setActiveSearchValue('');
                                        setShowSearchDoc(false);
                                    }}
                                >
                                    <IconX size={11} />
                                </ActionIcon>
                            }
                            className="flex-1 min-w-[140px]"
                        />
                    )}

                    {showSearchEmail && (
                        <TextInput
                            ref={searchEmailInputRef}
                            size="xs"
                            type="email"
                            placeholder={t('search_by_email')}
                            leftSection={<IconMail size={13} className="text-slate-400" />}
                            value={searchEmail}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSearchEmail(val);
                                setActiveSearchValue(val);
                                setIsDropdownOpen(true);
                                setHighlightedIndex(-1);
                            }}
                            onFocus={() => {
                                if (searchEmail.trim().length >= MIN_CHARS_TO_SEARCH || debouncedSearchQuery.length >= MIN_CHARS_TO_SEARCH) {
                                    setIsDropdownOpen(true);
                                }
                            }}
                            onKeyDown={handleKeyDown}
                            rightSection={
                                <ActionIcon
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    onClick={() => {
                                        setSearchEmail('');
                                        setActiveSearchValue('');
                                        setShowSearchEmail(false);
                                    }}
                                >
                                    <IconX size={11} />
                                </ActionIcon>
                            }
                            className="flex-1 min-w-[160px]"
                        />
                    )}
                </div>
            )}

            {/* Popover Search Engine Style Results Dropdown */}
            {shouldShowPopover && (
                <div className="mt-1.5 bg-white divide-y divide-slate-100 border border-slate-200 rounded-md overflow-hidden">
                    {/* Loading indicator */}
                    {isSearching && (
                        <div className="py-6 px-3 text-center text-[11px] text-slate-400 bg-white">
                            {t('searching')}...
                        </div>
                    )}

                    {/* Existing matches */}
                    {!isSearching && matchingPaxs.length > 0 && (
                        <>
                            {/* <div className="px-3 py-1.5 text-[11px] font-semibold text-slate-500 bg-slate-50/90 flex items-center justify-between">
                                <span className="flex items-center gap-1.5">
                                    <IconUsers size={13} className="text-slate-400" />
                                    {t('existing_pax_matches')}
                                </span>
                                <span className="text-[10px] text-slate-500 bg-slate-200/70 px-1.5 py-0.5 rounded-full font-mono">
                                    {matchingPaxs.length}
                                </span>
                            </div> */}

                            <div className="max-h-[190px] overflow-y-auto subtle-scrollbar divide-y divide-slate-100">
                                {matchingPaxs.map((pax, idx) => {
                                    const hasDoc = !!(pax.document_type || pax.document_number);
                                    const hasMail = !!pax.email;
                                    const hasPhone = !!pax.phone;
                                    const docStr = [pax.document_type, pax.document_number].filter(Boolean).join(' ');
                                    const isHighlighted = highlightedIndex === idx;

                                    return (
                                        <div
                                            key={pax.id}
                                            className={`p-2.5 transition-colors cursor-pointer flex items-center justify-between gap-3 group ${isHighlighted ? 'bg-blue-50/90' : 'hover:bg-blue-50/60'
                                                }`}
                                            onClick={() => handleSelectPax(pax)}
                                        >
                                            {/* Left Category & ID Badge */}
                                            <div className="flex flex-col items-center justify-center min-w-[38px] select-none">
                                                {pax.category && <TagIcon name={pax.category} hideText size="xs" />}
                                                <span className="font-light text-[9px] tracking-tight">#{pax.id}</span>
                                            </div>

                                            {/* Center Details */}
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-xs text-slate-900 group-hover:text-blue-700 transition-colors truncate">
                                                        {pax.name}
                                                    </span>
                                                    {pax.discount_category && (
                                                        <span className="px-1.5 py-0.2 rounded text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60 shrink-0">
                                                            {pax.discount_category}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs mt-0.5">
                                                    {hasDoc && (
                                                        <div className="flex items-center gap-1 text-[10px] text-slate-700 bg-slate-100/90 px-1 py-0.2 rounded border border-slate-200/60">
                                                            <IconId size={10} stroke={1.8} className="text-slate-400 shrink-0" />
                                                            <span>{docStr}</span>
                                                        </div>
                                                    )}
                                                    {hasPhone && (
                                                        <div className="flex items-center gap-1 text-[10px] text-slate-500">
                                                            <IconPhone size={10} stroke={1.8} className="text-slate-400 shrink-0" />
                                                            <span>{pax.phone}</span>
                                                        </div>
                                                    )}
                                                    {pax.phone_2 && (
                                                        <div className="flex items-center gap-1 text-[10px] text-slate-500">
                                                            <IconPhone size={10} stroke={1.8} className="text-slate-400 shrink-0" />
                                                            <span>{pax.phone_2}</span>
                                                        </div>
                                                    )}
                                                    {hasMail && (
                                                        <div className="flex items-center gap-1 text-[10px] text-slate-500 truncate max-w-[130px]">
                                                            <IconMail size={10} stroke={1.8} className="text-slate-400 shrink-0" />
                                                            <span className="truncate">{pax.email}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Right Select Affordance */}
                                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded transition-colors shrink-0 inline-flex items-center gap-1 ${isHighlighted ? 'text-blue-700 bg-blue-100/90' : 'text-slate-500 bg-slate-100 group-hover:text-blue-600 group-hover:bg-blue-100/70'}`}>
                                                <span>{t('select_and_add')}</span>
                                                {isHighlighted && <kbd className="font-mono text-[9px]">↵</kbd>}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* No results message */}
                    {!isSearching && matchingPaxs.length === 0 && (
                        <div className="py-6 px-3 text-center text-[11px] text-slate-400 bg-white">
                            {t('no_results')}
                        </div>
                    )}

                    {/* Bottom Create As New Pax Section with Inline Optional Fields */}
                    {canCreateNewPax && (
                        <div
                            className="p-3.5 bg-slate-50/90 border-t-2 border-slate-200"
                        >
                            {/* Top clickable action row */}
                            <div
                                onClick={() => setShowNewPaxSection((prev) => !prev)}
                                className={`flex items-center justify-between gap-2 cursor-pointer group ${showNewPaxSection ? 'pb-2.5 border-b border-slate-200/80' : ''}`}
                            >
                                <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
                                    <div className="w-4 h-4 rounded-full bg-slate-900 group-hover:bg-blue-600 text-white flex items-center justify-center shrink-0">
                                        <IconPlus size={12} stroke={2.5} />
                                    </div>
                                    <span className="text-sm text-slate-700 truncate">
                                        {t('new_person')}: <strong className="text-slate-900 font-semibold">{name.trim()}</strong>
                                    </span>
                                </div>
                                {showNewPaxSection ? (
                                    <IconChevronUp size={16} className="text-slate-400 group-hover:text-slate-600 shrink-0" />
                                ) : (
                                    <IconChevronDown size={16} className="text-slate-400 group-hover:text-slate-600 shrink-0" />
                                )}
                            </div>

                            {/* Form fields styled like PersonDetailsTab */}
                            {showNewPaxSection && (
                                <div
                                    className="space-y-2 mt-2.5"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 w-full">
                                        <PaxCategorySelect
                                            size="xs"
                                            value={category}
                                            onChange={(val) => setCategory(val)}
                                            disabled={createPersonMutation.isPending}
                                            onFocus={() => setHighlightedIndex(-1)}
                                            className="w-32 shrink-0"
                                        />

                                        <TextInput
                                            size="xs"
                                            type="text"
                                            placeholder={t('person_notes')}
                                            leftSection={<IconPencil size={14} className="text-gray-400" />}
                                            value={observations}
                                            disabled={createPersonMutation.isPending}
                                            onFocus={() => setHighlightedIndex(-1)}
                                            onChange={(e) => {
                                                setObservations(e.target.value);
                                            }}
                                            onKeyDown={handleKeyDown}
                                            className="font-semibold flex-1 min-w-[140px]"
                                        />

                                        <div className="flex items-center gap-1 shrink-0">
                                            {([
                                                { key: 'adults', val: adults, setter: setAdults },
                                                { key: 'children', val: children, setter: setChildren },
                                                { key: 'seniors', val: seniors, setter: setSeniors },
                                                { key: 'infants', val: infants, setter: setInfants },
                                                { key: 'guests', val: guests, setter: setGuests },
                                            ] as const).map(({ key, val, setter }) => (
                                                <Tooltip key={key} label={t(key)} position="top" withArrow openDelay={200}>
                                                    <div>
                                                        <NumberInput
                                                            size="xs"
                                                            min={0}
                                                            hideControls
                                                            leftSection={<PaxCategoryBadge category={key} />}
                                                            leftSectionWidth={26}
                                                            value={val}
                                                            disabled={createPersonMutation.isPending}
                                                            onFocus={() => setHighlightedIndex(-1)}
                                                            onChange={(v) => setter(v)}
                                                            onKeyDown={handleKeyDown}
                                                            className="font-semibold w-[52px]"
                                                            styles={{
                                                                input: {
                                                                    paddingLeft: 24,
                                                                    paddingRight: 4,
                                                                    textAlign: 'center',
                                                                },
                                                            }}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                                        <div className="flex items-center gap-1.5 flex-1 min-w-[130px]">
                                            <TextInput
                                                ref={phoneInputRef}
                                                size="xs"
                                                type="tel"
                                                placeholder={t('phone')}
                                                leftSection={<IconPhone size={15} className="text-blue-500" />}
                                                value={phone}
                                                disabled={createPersonMutation.isPending}
                                                onFocus={() => setHighlightedIndex(-1)}
                                                onChange={(e) => {
                                                    setPhone(e.target.value);
                                                }}
                                                onKeyDown={handleKeyDown}
                                                className="font-semibold flex-1 min-w-0"
                                            />
                                            {!showPhone2 && (
                                                <Tooltip label={t('add_second_phone')} withArrow>
                                                    <ActionIcon
                                                        size={30}
                                                        radius="md"
                                                        variant="light"
                                                        color="blue"
                                                        className="shrink-0"
                                                        onClick={() => {
                                                            setShowPhone2(true);
                                                            setTimeout(() => phone2InputRef.current?.focus(), 50);
                                                        }}
                                                    >
                                                        <IconPhonePlus size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            )}
                                        </div>
                                        {showPhone2 && (
                                            <div className="flex items-center gap-1.5 flex-1 min-w-[130px]">
                                                <TextInput
                                                    ref={phone2InputRef}
                                                    size="xs"
                                                    type="tel"
                                                    placeholder={t('phone_2')}
                                                    leftSection={<IconPhone size={15} className="text-blue-500" />}
                                                    value={phone_2}
                                                    disabled={createPersonMutation.isPending}
                                                    onFocus={() => setHighlightedIndex(-1)}
                                                    onChange={(e) => {
                                                        setPhone2(e.target.value);
                                                    }}
                                                    onKeyDown={handleKeyDown}
                                                    className="font-semibold flex-1 min-w-0"
                                                />
                                                <ActionIcon
                                                    size={30}
                                                    radius="md"
                                                    variant="subtle"
                                                    color="gray"
                                                    className="shrink-0"
                                                    onClick={() => {
                                                        setPhone2('');
                                                        setShowPhone2(false);
                                                    }}
                                                >
                                                    <IconX size={14} />
                                                </ActionIcon>
                                            </div>
                                        )}
                                        <TextInput
                                            ref={emailInputRef}
                                            size="xs"
                                            type="email"
                                            placeholder={t('email')}
                                            leftSection={<IconMail size={15} className="text-blue-500" />}
                                            value={email}
                                            disabled={createPersonMutation.isPending}
                                            onFocus={() => setHighlightedIndex(-1)}
                                            onChange={(e) => {
                                                setEmail(e.target.value);
                                            }}
                                            onKeyDown={handleKeyDown}
                                            className="flex-1 min-w-[130px]"
                                        />

                                        <TextInput
                                            ref={docInputRef}
                                            size="xs"
                                            placeholder={t('id_number')}
                                            leftSection={<IconId size={15} className="text-gray-400" />}
                                            value={document_number}
                                            disabled={createPersonMutation.isPending}
                                            onFocus={() => setHighlightedIndex(-1)}
                                            onChange={(e) => {
                                                setDocument(e.target.value);
                                            }}
                                            onKeyDown={handleKeyDown}
                                            className="flex-1 min-w-[120px]"
                                        />

                                        <Button
                                            onClick={save}
                                            loading={createPersonMutation.isPending}
                                            size="xs"
                                            radius="xl"
                                            color="black"
                                            leftSection={<IconUserPlus size={14} />}
                                            className="shrink-0 ml-auto"
                                        >
                                            {t('save')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}